import axios from "axios";
import { useEffect, useState } from "react";
import { CheckCircle, Eye, EyeSlash, Lock, XCircle } from "react-bootstrap-icons";
import { Link, useParams } from "react-router-dom";

import LoadingSpinner from "../../components/LoadingSpinner";
import { ApiConnectionString } from "../../constants/ApiConntection";

import "./ResetPasswordPage.css";

export default function ResetPasswordPage() {
  const { token } = useParams();

  const [isChecking, setIsChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [err, setErr] = useState("");
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    checkToken();
  }, [token]);

  const checkToken = async () => {
    try {
      setIsChecking(true);
      setErr("");

      const response = await axios.get(
        `${ApiConnectionString}/api/ForgotPassword/${token}`
      );

      setTokenValid(response.data === true || response.data === "true");
    } catch (error) {
      console.error(error);

      if (typeof error.response?.data === "string") {
        setErr(error.response.data);
      } else {
        setErr("This password reset link is invalid or has expired.");
      }

      setTokenValid(false);
    } finally {
      setIsChecking(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();

    setErr("");

    if (!password || !confirmPassword) {
      setErr("Please complete both password fields.");
      return;
    }

    if (password.length < 6) {
      setErr("Your new password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setErr("The passwords do not match.");
      return;
    }

    try {
      setIsSaving(true);

      await axios.put(
        `${ApiConnectionString}/api/ForgotPassword/resetpassword`,
        {
          token,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setComplete(true);
    } catch (error) {
      console.error(error);

      if (error.response?.data === "expired") {
        setErr("This password reset link has expired.");
      } else if (typeof error.response?.data === "string") {
        setErr(error.response.data);
      } else {
        setErr("Could not reset your password.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isChecking) {
    return (
      <div className="reset-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (complete) {
    return (
      <div className="reset-page">
        <div className="reset-card">
          <div className="reset-icon">
            <CheckCircle />
          </div>

          <h1>Password changed</h1>

          <p>Your password has been reset. You can now log in again.</p>

          <Link to="/" className="reset-primary-btn">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-page">
        <div className="reset-card">
          <div className="reset-icon reset-error-icon">
            <XCircle />
          </div>

          <h1>Reset link unavailable</h1>

          <p>{err || "This password reset link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-page">
      <div className="reset-card">
        <div className="reset-icon">
          <Lock />
        </div>

        <h1>Reset password</h1>

        <p>Enter your new password below.</p>

        {err && (
          <div className="reset-error">
            <XCircle />
            <span>{err}</span>
          </div>
        )}

        <form onSubmit={resetPassword}>
          <div className="reset-field">
            <label>New password</label>

            <div className="reset-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </button>
            </div>
          </div>

          <div className="reset-field">
            <label>Confirm new password</label>

            <div className="reset-password-wrap">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeSlash /> : <Eye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="reset-primary-btn"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Reset password"}
          </button>
        </form>
      </div>
    </div>
  );
}